import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FormSection } from '@praktio/common-ui';
import { Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep, isEqual } from 'lodash';
import { useParams } from 'react-router-dom';

import CourseTitle from '../../common/CourseTitle/CourseTitle';
import { Table } from '../../common/Table/Table';
import { RootState } from '../../../ducks/store';
import { updateTeamCourseAssignments } from '../../../ducks/teams';

const TeamCoursesHeaders = [
  {
    Header: 'Course',
    accessor: 'title',
    className: 'TeamCourses-course-name',
    Cell: (props: any) => {
      const original = props.row.original;
      const customDataForCell = props.customDataForCell;
      if (original) {
        return (
          <div className="TeamCourseCell d-flex justify-content-between">
            <CourseTitle course={original} />
            {customDataForCell && customDataForCell.editingCourseAssignments && (
              <span
                className="material-icons edit-assignment-status-button pointer"
                onClick={() => {
                  if (customDataForCell.onChange) {
                    customDataForCell.onChange({
                      fieldName: 'courseAssignment',
                      value: original,
                      tab: 'teamCourses',
                    });
                  }
                }}
              >
                {customDataForCell.assignedToTeam ? 'close' : 'add'}
              </span>
            )}
          </div>
        );
      }
    },
  },
];

interface ITeamCoursesTabsProps {
  notifyHasChanges: (changes: boolean) => void;
  setSavedChanges: (success: boolean) => void;
  editing: boolean;
}

const TeamCoursesTab = forwardRef<IForwardProps, ITeamCoursesTabsProps>(
  ({ notifyHasChanges, setSavedChanges, editing }, ref) => {
    useImperativeHandle(ref, () => ({
      save,
    }));

    const params = useParams();
    const teamId = params.teamId as string;

    const dispatch = useDispatch();

    const { initialAssigned, initialUnassigned } = useSelector((state: RootState) => {
      const { teams, courses } = state;

      const initialAssigned: ICourse[] = [];
      const initialUnassigned: ICourse[] = [];

      (courses.list as ICourse[])?.forEach((course) => {
        const hasCourse = !!(teams.current?.courses as ICourse[])?.find(
          (c) => c._id === course._id,
        );
        if (hasCourse) {
          initialAssigned.push(course);
        } else {
          initialUnassigned.push(course);
        }
      });

      return {
        initialAssigned,
        initialUnassigned,
      };
    });

    const prevAssigned = useRef<ICourse[]>(initialAssigned);
    const prevUnassigned = useRef<ICourse[]>(initialUnassigned);
    const [assigned, setAssigned] = useState<ICourse[]>(initialAssigned);
    const [unassigned, setUnassigned] = useState<ICourse[]>(initialUnassigned);
    const [courseAssignmentUpdates, setCourseAssignmentUpdates] = useState<ICourse[]>([]);

    useEffect(() => {
      if (
        (initialAssigned && !isEqual(prevAssigned.current, initialAssigned)) ||
        (initialUnassigned && !isEqual(prevUnassigned.current, initialUnassigned))
      ) {
        setAssigned(initialAssigned);
        setUnassigned(initialUnassigned);
        setCourseAssignmentUpdates([]);
        if (notifyHasChanges) {
          notifyHasChanges(false);
        }
      }
      prevAssigned.current = initialAssigned;
      prevUnassigned.current = initialUnassigned;
    }, [initialAssigned, initialUnassigned]);

    // Used to reset the lists if editing gets cancelled
    useEffect(() => {
      if (!editing) {
        setAssigned(prevAssigned.current);
        setUnassigned(prevUnassigned.current);
      }
    }, [editing]);

    useEffect(() => {
      const hasChanges = courseAssignmentUpdates.length > 0;
      notifyHasChanges(hasChanges);
    }, [courseAssignmentUpdates, notifyHasChanges]);

    // Used by parent component to initiate a save
    const save = async () => {
      try {
        await dispatch(updateTeamCourseAssignments(teamId, assigned));
        setSavedChanges(true);
      } catch (e) {
        setSavedChanges(false);
      }
    };

    const onAssignmentChange = ({
      assigning,
      source,
      target,
      course,
    }: {
      assigning: boolean;
      source: ICourse[];
      target: ICourse[];
      course: ICourse;
    }) => {
      const updatedSource = cloneDeep(source);
      const updatedTarget = cloneDeep(target);
      let index = updatedSource.findIndex((c) => c._id === course._id);
      if (index > -1) {
        updatedSource.splice(index, 1);
      }

      index = updatedTarget.findIndex((c) => c._id === course._id);
      if (index === -1) {
        updatedTarget.push(course);
      }
      const newCourseAssignmentUpdates = cloneDeep(courseAssignmentUpdates);
      const coursePresentInAssigned = prevAssigned.current.some(
        (prevAssignedCourse) => prevAssignedCourse._id === course._id,
      );
      const coursePresentInUnassigned = prevUnassigned.current.some(
        (prevUnassignedCourse) => prevUnassignedCourse._id === course._id,
      );
      if ((assigning && !coursePresentInAssigned) || (!assigning && !coursePresentInUnassigned)) {
        newCourseAssignmentUpdates.push(course);
      } else {
        const indexOfCourseInUpdates = newCourseAssignmentUpdates.findIndex(
          (updatedCourse) => updatedCourse._id === course._id,
        );
        newCourseAssignmentUpdates.splice(indexOfCourseInUpdates, 1);
      }
      setCourseAssignmentUpdates(newCourseAssignmentUpdates);

      return { updatedSource, updatedTarget };
    };

    const unassignCourse = ({ fieldName, value }: { fieldName?: string; value?: ICourse }) => {
      const results = onAssignmentChange({
        assigning: false,
        source: assigned,
        target: unassigned,
        course: value as ICourse,
      });

      setAssigned(results.updatedSource);
      setUnassigned(results.updatedTarget);
    };

    const assignCourse = ({ fieldName, value }: { fieldName?: string; value?: ICourse }) => {
      const results = onAssignmentChange({
        assigning: true,
        source: unassigned,
        target: assigned,
        course: value as ICourse,
      });

      setUnassigned(results.updatedSource);
      setAssigned(results.updatedTarget);
    };

    const processedAssigned = assigned.map((assignedCourse) => {
      const updatedAssignedCourse = cloneDeep(assignedCourse);
      const prevValueOfCourse = prevAssigned.current.find(
        (prevAssignedCourse) => prevAssignedCourse._id === assignedCourse.id,
      );
      if (prevValueOfCourse?.markedForRemoval) {
        updatedAssignedCourse.markedForRemoval = false;
      } else {
        delete updatedAssignedCourse.markedForRemoval;
      }
      const isChanged = !prevAssigned.current.some(
        (prevAssignedCourse) => prevAssignedCourse._id === assignedCourse._id,
      );
      if (isChanged) {
        updatedAssignedCourse.isChanged = true;
      } else {
        updatedAssignedCourse.isChanged = false;
      }
      return updatedAssignedCourse;
    });

    const processedUnassigned = unassigned.map((unassignedCourse) => {
      const updatedUnassignedCourse = cloneDeep(unassignedCourse);
      const prevValueOfCourse = prevUnassigned.current.find(
        (prevUnassignedCourse) => prevUnassignedCourse._id === unassignedCourse._id,
      );
      if (prevValueOfCourse?.isChanged) {
        updatedUnassignedCourse.isChanged = false;
      } else {
        delete updatedUnassignedCourse.isChanged;
      }
      const markedForRemoval = !prevUnassigned.current.some(
        (prevUnassignedCourse) => prevUnassignedCourse._id === unassignedCourse._id,
      );
      if (markedForRemoval) {
        updatedUnassignedCourse.markedForRemoval = true;
      } else {
        updatedUnassignedCourse.markedForRemoval = false;
      }
      return updatedUnassignedCourse;
    });

    return (
      <FormSection sectionName="Courses">
        <Row className="Team-courses-tables">
          <Col xs={12} md={{ span: editing ? 6 : 12 }}>
            <Table
              columns={TeamCoursesHeaders}
              data={processedAssigned}
              tableTitle="Assigned Courses"
              customDataForCell={{
                editingCourseAssignments: editing,
                assignedToTeam: true,
                prevAssignedCourseList: prevAssigned.current,
                onChange: unassignCourse,
              }}
            />
          </Col>
          <Col xs={12} md={6}>
            {editing && (
              <Table
                columns={TeamCoursesHeaders}
                data={processedUnassigned}
                tableTitle="Unssigned Courses"
                customDataForCell={{
                  editingCourseAssignments: editing,
                  assignedToTeam: false,
                  onChange: assignCourse,
                }}
              />
            )}
          </Col>
        </Row>
      </FormSection>
    );
  },
);

export default TeamCoursesTab;
